import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ChatWidget from "@/components/ChatWidget";

export const CRM_URL = process.env.REACT_APP_CRM_URL || "https://crm.phillyrideshare.com";

// Public Pages
import LandingPage from "@/pages/LandingPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";

// Admin CRM
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminVehicles from "@/pages/admin/AdminVehicles";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminReminders from "@/pages/admin/AdminReminders";

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// External redirect to the CRM booking flow
const CrmRedirect = () => {
  useEffect(() => {
    window.location.replace(`${CRM_URL}/book`);
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-500">
      Redirecting to booking…
    </div>
  );
};

const CrmVehicleRedirect = () => {
  const { vehicleId } = useParams();
  useEffect(() => {
    window.location.replace(`${CRM_URL}/book/${vehicleId}`);
  }, [vehicleId]);
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-500">
      Redirecting to booking…
    </div>
  );
};

const CrmLoginRedirect = () => {
  useEffect(() => {
    window.location.replace(`${CRM_URL}/login`);
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-500">
      Redirecting to login…
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/fleet" element={<CrmRedirect />} />
      <Route path="/book" element={<CrmRedirect />} />
      <Route path="/book/:vehicleId" element={<CrmVehicleRedirect />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      
      {/* Auth Routes — redirected to the CRM */}
      <Route path="/login" element={<CrmLoginRedirect />} />
      <Route path="/register" element={<CrmLoginRedirect />} />
      
      {/* Customer Portal — redirected to the CRM */}
      <Route path="/dashboard" element={<CrmLoginRedirect />} />
      
      {/* Admin CRM Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/customers" element={
        <ProtectedRoute requiredRole="admin">
          <AdminCustomers />
        </ProtectedRoute>
      } />
      <Route path="/admin/bookings" element={
        <ProtectedRoute requiredRole="admin">
          <AdminBookings />
        </ProtectedRoute>
      } />
      <Route path="/admin/vehicles" element={
        <ProtectedRoute requiredRole="admin">
          <AdminVehicles />
        </ProtectedRoute>
      } />
      <Route path="/admin/leads" element={
        <ProtectedRoute requiredRole="admin">
          <AdminLeads />
        </ProtectedRoute>
      } />
      <Route path="/admin/payments" element={
        <ProtectedRoute requiredRole="admin">
          <AdminPayments />
        </ProtectedRoute>
      } />
      <Route path="/admin/reminders" element={
        <ProtectedRoute requiredRole="admin">
          <AdminReminders />
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <ChatWidget />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
